import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon } from '../components/Icon';
import { Loading, ErrorView } from '../components/ViewState';
import { api } from '../data/api';
import type { OpsData, OpsItem } from '../data/types';

function expiryBadge(days: number | null): { label: string; cls: string } {
  if (days == null) return { label: '—', cls: '' };
  if (days < 0) return { label: `scaduto da ${-days}g`, cls: 'exp-bad' };
  if (days <= 7) return { label: `tra ${days}g`, cls: 'exp-bad' };
  if (days <= 30) return { label: `tra ${days}g`, cls: 'exp-warn' };
  return { label: `tra ${days}g`, cls: 'exp-ok' };
}

interface Props {
  onOpenClient: (id: string) => void;
}

export function OperativitaView({ onOpenClient }: Props) {
  const [data, setData] = useState<OpsData | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      setData(await api.monitoring());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const runChecks = async () => {
    setBusy(true);
    try {
      await api.runChecks();
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (err) return <ErrorView detail={err} />;
  if (!data) return <Loading />;

  const Item = ({ i, right }: { i: OpsItem; right: ReactNode }) => (
    <button type="button" className="ops-item" onClick={() => onOpenClient(i.id)}>
      <span className="rs-av" style={{ width: 30, height: 30, fontSize: 12, '--av': i.accent ?? '#BB6E48' } as CSSProperties} aria-hidden="true">
        {i.mono ?? i.name[0]}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, textAlign: 'left' }}>
        <span className="ops-name">{i.name}</span>
        <span className="rs-cli-meta">{[i.category, i.locality].filter(Boolean).join(' · ') || i.domain}</span>
      </span>
      {right}
    </button>
  );

  return (
    <div className="view">
      <div className="view-pad rs-stack">
        <div className="rs-actionbar split">
          <span className="rs-muted" style={{ fontSize: 13 }}>{data.checked}/{data.total} siti controllati</span>
          <button type="button" className="btn btn-ghost" onClick={runChecks} disabled={busy}>
            <Icon name="spark" size={15} /> {busy ? 'Controllo…' : 'Controlla ora'}
          </button>
        </div>

        <div className="rs-2col">
          <div className="rs-card ops-card crit">
            <div className="ops-head">
              <Icon name="alert" size={16} /> Siti giù <span className="ops-n">{data.sitesDown.length}</span>
            </div>
            {data.sitesDown.length ? (
              data.sitesDown.map((i) => {
                const why = i.httpStatus == null ? 'non raggiungibile' : i.httpStatus >= 500 ? `errore ${i.httpStatus}` : `HTTP ${i.httpStatus}`;
                return <Item key={i.id} i={i} right={<span className="ops-tag ops-down">{why}</span>} />;
              })
            ) : (
              <div className="ops-empty">Tutti i siti online ✓</div>
            )}
          </div>

          <div className="rs-card ops-card">
            <div className="ops-head">
              <Icon name="globe" size={16} /> Domini in scadenza <span className="ops-n">{data.domainsExpiring.length}</span>
            </div>
            {data.domainsExpiring.length ? (
              data.domainsExpiring.map((i) => {
                const b = expiryBadge(i.domainDays);
                return <Item key={i.id} i={i} right={<span className={'ops-tag ' + b.cls}>{b.label}</span>} />;
              })
            ) : (
              <div className="ops-empty">Nessun dominio in scadenza nei prossimi 90 giorni.</div>
            )}
          </div>
        </div>

        <div className="rs-2col">
          <div className="rs-card ops-card">
            <div className="ops-head">
              <Icon name="chart" size={16} /> Da incassare <span className="ops-n">{data.unpaid.length}</span>
            </div>
            {data.unpaid.length ? (
              data.unpaid.map((i) => (
                <Item key={i.id} i={i} right={<span className="ops-tag exp-warn">{i.health === 'overdue' ? 'insoluto' : 'setup'}</span>} />
              ))
            ) : (
              <div className="ops-empty">Tutto incassato ✓</div>
            )}
          </div>

          <div className="rs-card ops-card">
            <div className="ops-head">
              <Icon name="check" size={16} /> Salute clienti
            </div>
            {data.lowHealth.length ? (
              data.lowHealth.map((i) => (
                <Item
                  key={i.id}
                  i={i}
                  right={<span className={'ops-score ' + (i.score >= 80 ? 'good' : i.score >= 60 ? 'warn' : 'bad')}>{i.score}</span>}
                />
              ))
            ) : (
              <div className="ops-empty">Nessun cliente attivo.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
